import { h, Ref } from 'vue';
import { dateTimeField, dropdownField, inputField } from '@/ui/components/form';
import { Task, useValidation } from '@/ui/concerns';
import { IssueDetailData, validate } from '@/entities/issue/public';
import { apiErrorMessage } from '@/ui/components';
import { t } from '@/ui/i18n/i18n';
import AddPhotos from '@/_legacy/components/Issues/AddPhotos.vue';
import StatusSelector from '@/_legacy/components/Issues/StatusSelector.vue';
import PrioritySelector from '@/_legacy/components/Issues/PrioritySelector.vue';
import { Field } from '@/ui/components/form/Field';
import { hasPermission, Permissions } from '@/ui/auth/auth';
import AssigneesField from '@/ui/pages/user/assignees/AssigneesField.vue';

export function useIssueForm(record: Ref<IssueDetailData>, task: Task, disableStatusUpdate: Ref<boolean>) {
  const {
    validationErrors,
    hasNoValidationErrors,
    showValidationErrors,
  } = useValidation<IssueDetailData>(validate, record, task.state);

  const handleSubmit = () => {
    showValidationErrors.value = true;
    if (hasNoValidationErrors.value) {
      task.run();
    }
  };

  const formFields = () => [
    inputField(
      {
        name: 'title',
        label: t('common.title'),
        placeholder: t('issues.form.enter-title'),
        value: record.value.title,
        required: true,
        errors: validationErrors.value,
        disabled: true,
      }, () => {},
    ),

    inputField(
      {
        type: 'textarea',
        name: 'description',
        label: t('common.description'),
        placeholder: t('issues.form.enter-description'),
        value: record.value.description ?? '',
        required: false,
        errors: validationErrors.value,
        disabled: true,
      }, () => {},
    ),
    h(Field, {
      props: {
        name: 'photos',
        label: '',
        required: false,
        errors: validationErrors.value,
        hideLabel: true,
        disabled: true,
      },
    }, [h(AddPhotos, {
      props: {
        value: record.value.photos ?? [],
        disabled: true,
      },
    })]),

    h(Field, {
      props: {
        name: 'status',
        label: t('common.status'),
        required: false,
        errors: validationErrors.value,
        disabled: task.state.value.pending || disableStatusUpdate.value,
      },
    }, [h(StatusSelector, {
      props: {
        value: record.value.status,
        disabled: task.state.value.pending || disableStatusUpdate.value,
      },
      on: {
        input: (value: number) => {
          record.value.status = value;
        },
      },
    })]),

    record.value.entityItem
      ? dropdownField<string>({
        name: 'entity_item_id',
        label: t('common.entity-item'),
        value: record.value.entityItem ? record.value.entityItem.title : '',
        placeholder: t('sensors.form.enter-entity-item'),
        required: false,
        options: record.value.entityItem
          ? [{ label: record.value.entityItem.title.toString(), value: record.value.entityItem.title }]
          : [],
        errors: validationErrors.value,
        disabled: true,
      }, () => {})
      : null,

    ...(hasPermission(Permissions.CREATE_ISSUE_WITH_ASSIGNEE)
      ? [h(AssigneesField, {
        props: {
          value: record.value.associateAssignees,
          options: [],
          errors: validationErrors.value,
          disabled: true,
          disabledExternalAssignee: true,
        },
      })]
      : []),

    h(Field, {
      props: {
        name: 'priority',
        label: t('common.priority'),
        required: false,
        errors: validationErrors.value,
        disabled: true,
      },
    }, [h(PrioritySelector, {
      props: {
        value: record.value.priority,
        disabled: true,
      },
    })]),

    dateTimeField(
      {
        name: 'deadline',
        label: t('common.deadline'),
        value: record.value.deadline,
        placeholder: t('issues.form.enter-deadline'),
        required: false,
        errors: validationErrors.value,
        disabled: true,
        clearable: true,
        mobileNative: false,
      }, () => {},
    ),

    apiErrorMessage(task.state.value),
  ];

  return {
    formFields,
    handleSubmit,
  };
}
